<script setup lang="ts">
import { ref, computed } from 'vue'
import { useI18n } from 'vue-i18n'
import type { SavedPaymentMethod } from '@vpn-tg/vue-client-grpc/proto/savedPaymentMethod/savedPaymentMethod-endpoint'
import { usePayStore } from '@/modules/pay/store/payStore'
import { useFeedbackStore } from '@/modules/feedback/stores/feedbackStore'
import { useUserStore } from '@/modules/user/stores/userStore'
import { useApiStore } from '@/modules/api/store/apiStore'
import { formatDate, protoTimestampToMs } from '@/utils/dateFormatters'
import { resolveNextChargeAt } from '@/utils/resolveNextChargeAt'

const props = defineProps<{
  paymentMethod: SavedPaymentMethod
}>()

const { t, locale } = useI18n()
const payStore = usePayStore()
const feedbackStore = useFeedbackStore()
const userStore = useUserStore()
const apiStore = useApiStore()

const isToggling = ref(false)
const isDeleting = ref(false)
const showDeleteConfirm = ref(false)

const formattedExpiry = computed(() => {
  const month = props.paymentMethod.cardExpiryMonth?.padStart(2, '0') || '••'
  const year = props.paymentMethod.cardExpiryYear?.slice(-2) || '••'
  return `${month}/${year}`
})

const cardTypeNormalized = computed(() => {
  const type = (props.paymentMethod.cardType || '').toLowerCase()
  if (type.includes('mir')) return 'mir'
  if (type.includes('visa')) return 'visa'
  if (type.includes('mastercard') || type.includes('master')) return 'mastercard'
  return 'default'
})

const currentTariff = computed(() => {
  const tariffId = userStore.user?.lastTariffID
  if (!tariffId) return null
  return apiStore.tariffs.find((tariff) => tariff.id === tariffId) ?? null
})

/** Сумма следующего списания: для промо — цена fallback-тарифа, иначе цена текущего */
const nextChargeAmount = computed(() => {
  const tariff = currentTariff.value
  if (!tariff) return null

  if (tariff.isPromo && tariff.fallbackTariffID) {
    const fallback = apiStore.tariffs.find((item) => item.id === tariff.fallbackTariffID)
    return fallback?.price ?? null
  }

  return tariff.price
})

const autoRenewDesc = computed(() => {
  if (!props.paymentMethod.isAutoRenew) {
    const rawExpires = userStore.user?.lastTariffExpiresAt
    let expiresAt: number | null = null
    if (rawExpires != null) {
      if (typeof rawExpires === 'object' && 'seconds' in rawExpires) {
        expiresAt = protoTimestampToMs(rawExpires as any)
      } else {
        expiresAt = Number(rawExpires)
      }
    }

    if (expiresAt != null && !isNaN(expiresAt)) {
      return t('profile.autoRenewDisabledDesc', {
        date: formatDate(expiresAt, true, locale.value),
      })
    }
    return t('profile.autoRenewDisabledFallback')
  }

  const chargeAt = resolveNextChargeAt(
    userStore.user?.nextChargeAt,
    userStore.user?.lastTariffExpiresAt,
  )
  const amount = nextChargeAmount.value
  if (chargeAt == null || amount == null) {
    return t('profile.autoRenewDescFallback')
  }
  return t('profile.autoRenewDesc', {
    date: formatDate(chargeAt, true, locale.value),
    amount,
  })
})

const handleToggleAutoRenew = async () => {
  if (isToggling.value) return
  isToggling.value = true
  const val = !props.paymentMethod.isAutoRenew

  try {
    const res = await payStore.toggleAutoRenew(props.paymentMethod.id, val)
    if (res.ok) {
      feedbackStore.showToast({
        type: 'success',
        message: t('profile.autoRenewUpdated'),
      })
      await userStore.getUserData()
    } else {
      feedbackStore.showToast({
        type: 'error',
        message: res.error || t('profile.autoRenewError'),
      })
    }
  } catch (err: any) {
    feedbackStore.showToast({
      type: 'error',
      message: err.message || t('profile.autoRenewError'),
    })
  } finally {
    isToggling.value = false
  }
}

const handleDelete = async () => {
  isDeleting.value = true
  try {
    const res = await payStore.deleteSavedPaymentMethod(props.paymentMethod.id)
    if (res.ok) {
      feedbackStore.showToast({
        type: 'success',
        message: t('profile.unlinkSuccess'),
      })
      showDeleteConfirm.value = false
    } else {
      feedbackStore.showToast({
        type: 'error',
        message: res.error || t('profile.unlinkError'),
      })
    }
  } catch (err: any) {
    feedbackStore.showToast({
      type: 'error',
      message: err.message || t('profile.unlinkError'),
    })
  } finally {
    isDeleting.value = false
  }
}
</script>

<template>
  <v-card color="surface" rounded="xl" class="pa-4 saved-card-card">
    <div class="saved-card-layout">
      <!-- Левая колонка: банковская карта -->
      <div class="saved-card-layout__left">
        <div class="bank-card" :class="`bank-card--${cardTypeNormalized}`">
          <!-- Фоновые неоновые блики -->
          <div class="bank-card__glow"></div>
          <div class="bank-card__pattern"></div>

          <!-- Верхняя линия: чип, contactless и логотип платежной системы -->
          <div class="bank-card__header d-flex align-center justify-space-between">
            <div class="d-flex align-center ga-3">
              <!-- Золотой EMV чип -->
              <div class="emv-chip">
                <div class="emv-chip__line emv-chip__line--h"></div>
                <div class="emv-chip__line emv-chip__line--v1"></div>
                <div class="emv-chip__line emv-chip__line--v2"></div>
                <div class="emv-chip__core"></div>
              </div>
              <!-- Иконка бесконтактной оплаты -->
              <i class="ri-rfid-line bank-card__nfc"></i>
            </div>

            <!-- Логотип платежной системы -->
            <div class="bank-card__brand">
              <template v-if="cardTypeNormalized === 'mir'">
                <span class="brand-mir">МИР</span>
              </template>
              <template v-else-if="cardTypeNormalized === 'visa'">
                <span class="brand-visa">VISA</span>
              </template>
              <template v-else-if="cardTypeNormalized === 'mastercard'">
                <div class="brand-mc">
                  <span class="brand-mc__circle brand-mc__circle--red"></span>
                  <span class="brand-mc__circle brand-mc__circle--orange"></span>
                </div>
              </template>
              <template v-else>
                <i class="ri-bank-card-2-line brand-icon"></i>
              </template>
            </div>
          </div>

          <!-- Номер карты с маской -->
          <div class="bank-card__number">
            <span class="dots">••••</span>
            <span class="dots">••••</span>
            <span class="dots">••••</span>
            <span class="digits">{{ paymentMethod.cardLast4 || '••••' }}</span>
          </div>

          <!-- Нижняя строка: срок действия и статус автопродления -->
          <div class="bank-card__footer d-flex align-end justify-space-between">
            <div class="d-flex flex-column">
              <span class="bank-card__label">VALID THRU</span>
              <span class="bank-card__value">{{ formattedExpiry }}</span>
            </div>

            <div class="bank-card__status d-flex align-center ga-1">
              <span
                class="status-dot"
                :class="{ 'status-dot--active': paymentMethod.isAutoRenew }"
              ></span>
              <span class="status-text">
                {{ paymentMethod.isAutoRenew ? t('profile.active') : t('profile.inactive') }}
              </span>
            </div>
          </div>
        </div>
      </div>

      <!-- Правая колонка: премиальный виджет управления автопродлением и отвязкой -->
      <div class="saved-card-layout__right">
        <!-- Плашка автопродления — переключает только свитч -->
        <div
          class="autorenew-widget"
          :class="{ 'autorenew-widget--active': paymentMethod.isAutoRenew }"
        >
          <div class="d-flex align-center justify-space-between w-100 mb-2">
            <div class="d-flex align-center ga-2">
              <div class="autorenew-widget__icon">
                <i class="ri-refresh-line"></i>
              </div>
              <span class="autorenew-widget__title">{{ t('profile.autoRenew') }}</span>
            </div>

            <button
              type="button"
              class="neon-switch"
              :class="{ 'neon-switch--active': paymentMethod.isAutoRenew, 'neon-switch--loading': isToggling }"
              :disabled="isToggling"
              :aria-pressed="paymentMethod.isAutoRenew"
              :aria-label="t('profile.autoRenew')"
              @click.stop="handleToggleAutoRenew"
            >
              <div class="neon-switch__track"></div>
              <div class="neon-switch__thumb">
                <i v-if="isToggling" class="ri-loader-4-line neon-switch__spinner"></i>
              </div>
            </button>
          </div>

          <span class="autorenew-widget__desc">
            {{ autoRenewDesc }}
          </span>
        </div>

        <!-- Нижняя панель: безопасность и действие отвязки -->
        <div class="saved-card-actions d-flex align-center justify-space-between ga-2">
          <div class="security-badge d-flex align-center ga-1">
            <i class="ri-shield-check-line"></i>
            <span>ЮKassa PCI DSS</span>
          </div>

          <button
            type="button"
            class="unlink-card-btn d-flex align-center ga-1"
            :disabled="isToggling"
            @click.stop="showDeleteConfirm = true"
          >
            <i class="ri-link-unlink-m"></i>
            <span>{{ t('profile.unlinkCard') }}</span>
          </button>
        </div>
      </div>
    </div>

    <!-- Диалог подтверждения отвязки карты -->
    <v-dialog v-model="showDeleteConfirm" max-width="380">
      <v-card color="surface" rounded="xl" class="pa-4">
        <div class="d-flex flex-column ga-4">
          <span class="text-h4">{{ t('profile.unlinkCardConfirmTitle') }}</span>
          <span class="text-body-2 text-medium-emphasis">
            {{ t('profile.unlinkCardConfirmDesc') }}
          </span>
          <div class="d-flex flex-column ga-2">
            <v-btn
              block
              color="error"
              :loading="isDeleting"
              @click="handleDelete"
            >
              {{ t('profile.unlinkCard') }}
            </v-btn>
            <v-btn
              block
              variant="text"
              :disabled="isDeleting"
              @click="showDeleteConfirm = false"
            >
              {{ t('common.cancel') }}
            </v-btn>
          </div>
        </div>
      </v-card>
    </v-dialog>
  </v-card>
</template>

<style scoped>
.saved-card-card {
  width: 100%;
}

.saved-card-layout {
  display: flex;
  align-items: stretch;
  gap: 16px;
  width: 100%;
}

.saved-card-layout__left {
  flex: 0 0 260px;
  max-width: 260px;
}

.saved-card-layout__right {
  flex: 1 1 auto;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  gap: 12px;
  min-width: 0;
}

/* Виджет автопродления */
.autorenew-widget {
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 14px;
  padding: 12px 14px;
  user-select: none;
  transition: all 0.25s ease;
  display: flex;
  flex-direction: column;
  justify-content: center;
  flex-grow: 1;
}

.autorenew-widget--active {
  background: linear-gradient(135deg, rgba(34, 197, 94, 0.08) 0%, rgba(255, 255, 255, 0.02) 100%);
  border-color: rgba(74, 222, 128, 0.28);
}

.autorenew-widget__icon {
  width: 32px;
  height: 32px;
  border-radius: 9px;
  background: rgba(255, 255, 255, 0.06);
  color: rgba(255, 255, 255, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
  transition: all 0.25s ease;
}

.autorenew-widget--active .autorenew-widget__icon {
  background: rgba(74, 222, 128, 0.15);
  color: #4ade80;
  box-shadow: 0 0 12px rgba(74, 222, 128, 0.25);
}

.autorenew-widget__title {
  font-size: 13px;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.95);
  line-height: 1.2;
}

.autorenew-widget__dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: #ef4444;
  box-shadow: 0 0 6px #ef4444;
}

.autorenew-widget__dot--active {
  background: #4ade80;
  box-shadow: 0 0 6px #4ade80;
}

.autorenew-widget__status {
  font-size: 11px;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.6);
}

.autorenew-widget--active .autorenew-widget__status {
  color: #4ade80;
}

.autorenew-widget__desc {
  font-size: 11px;
  line-height: 1.35;
  color: rgba(255, 255, 255, 0.5);
}

/* Кастомный неоновый свитч */
.neon-switch {
  position: relative;
  width: 44px;
  height: 24px;
  flex-shrink: 0;
  padding: 0;
  border: none;
  background: transparent;
  cursor: pointer;
}

.neon-switch:disabled {
  cursor: not-allowed;
  opacity: 0.7;
}

.neon-switch__track {
  width: 100%;
  height: 100%;
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.12);
  transition: all 0.25s ease;
  border: 1px solid rgba(255, 255, 255, 0.08);
}

.neon-switch--active .neon-switch__track {
  background: #22c55e;
  border-color: rgba(74, 222, 128, 0.5);
  box-shadow: 0 0 12px rgba(34, 197, 94, 0.4);
}

.neon-switch__thumb {
  position: absolute;
  top: 3px;
  left: 3px;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background: #ffffff;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.35);
  transition: transform 0.25s cubic-bezier(0.4, 0, 0.2, 1);
  display: flex;
  align-items: center;
  justify-content: center;
}

.neon-switch--active .neon-switch__thumb {
  transform: translateX(20px);
}

.neon-switch__spinner {
  font-size: 12px;
  color: #151822;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

/* Нижняя панель действий */
.saved-card-actions {
  padding-top: 2px;
}

.security-badge {
  font-size: 11px;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.45);
}

.security-badge i {
  color: #4ade80;
  font-size: 13px;
}

.unlink-card-btn {
  display: flex;
  align-items: center;
  background: rgba(239, 68, 68, 0.08);
  border: 1px solid rgba(239, 68, 68, 0.2);
  color: #f87171;
  padding: 6px 12px;
  border-radius: 10px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
}

.unlink-card-btn:hover:not(:disabled) {
  background: rgba(239, 68, 68, 0.18);
  border-color: rgba(239, 68, 68, 0.4);
  color: #ff8585;
  box-shadow: 0 0 12px rgba(239, 68, 68, 0.2);
  transform: translateY(-1px);
}

.unlink-card-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

@media (max-width: 600px) {
  .saved-card-layout {
    flex-direction: column;
  }

  .saved-card-layout__left {
    flex: 1 1 auto;
    max-width: 100%;
  }
}

/* Банковская карта в неоновом / киберпанк стиле */
.bank-card {
  position: relative;
  width: 100%;
  aspect-ratio: 1.586 / 1;
  max-width: 360px;
  margin: 0;
  padding: 18px;
  border-radius: 16px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  background: linear-gradient(135deg, #131722 0%, #1e2438 50%, #0d111a 100%);
  border: 1px solid rgba(255, 255, 255, 0.12);
  box-shadow: 0 12px 32px rgba(0, 0, 0, 0.4), inset 0 1px 0 rgba(255, 255, 255, 0.15);
  color: #fff;
  transition: transform 0.25s ease, box-shadow 0.25s ease;
}

.bank-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 16px 40px rgba(0, 0, 0, 0.5), inset 0 1px 0 rgba(255, 255, 255, 0.2);
}

/* Цветовые вариации по платежной системе */
.bank-card--mir {
  background: linear-gradient(135deg, #0e271f 0%, #13382c 50%, #0a1b15 100%);
  border-color: rgba(74, 222, 128, 0.25);
  box-shadow: 0 12px 32px rgba(10, 40, 25, 0.5), inset 0 1px 0 rgba(74, 222, 128, 0.2);
}

.bank-card--visa {
  background: linear-gradient(135deg, #101c3d 0%, #182b5c 50%, #0c142b 100%);
  border-color: rgba(96, 165, 250, 0.25);
  box-shadow: 0 12px 32px rgba(15, 35, 75, 0.5), inset 0 1px 0 rgba(96, 165, 250, 0.2);
}

.bank-card--mastercard {
  background: linear-gradient(135deg, #2b1318 0%, #3d1b22 50%, #1c0c10 100%);
  border-color: rgba(248, 113, 113, 0.25);
  box-shadow: 0 12px 32px rgba(50, 15, 20, 0.5), inset 0 1px 0 rgba(248, 113, 113, 0.2);
}

/* Неоновый блик на карте */
.bank-card__glow {
  position: absolute;
  top: -40%;
  right: -20%;
  width: 200px;
  height: 200px;
  border-radius: 50%;
  background: radial-gradient(circle, rgba(120, 119, 198, 0.25) 0%, transparent 70%);
  pointer-events: none;
}

.bank-card--mir .bank-card__glow {
  background: radial-gradient(circle, rgba(74, 222, 128, 0.25) 0%, transparent 70%);
}

.bank-card--visa .bank-card__glow {
  background: radial-gradient(circle, rgba(96, 165, 250, 0.25) 0%, transparent 70%);
}

.bank-card--mastercard .bank-card__glow {
  background: radial-gradient(circle, rgba(251, 146, 60, 0.25) 0%, transparent 70%);
}

/* Фоновый узор */
.bank-card__pattern {
  position: absolute;
  inset: 0;
  opacity: 0.04;
  background-image: radial-gradient(#fff 1px, transparent 1px);
  background-size: 12px 12px;
  pointer-events: none;
}

/* EMV Chip */
.emv-chip {
  position: relative;
  width: 38px;
  height: 28px;
  border-radius: 5px;
  background: linear-gradient(135deg, #d4af37 0%, #f9e276 50%, #aa820a 100%);
  border: 1px solid rgba(255, 255, 255, 0.4);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
  overflow: hidden;
}

.emv-chip__line {
  position: absolute;
  background: rgba(80, 50, 0, 0.45);
}

.emv-chip__line--h {
  top: 50%;
  left: 0;
  right: 0;
  height: 1px;
}

.emv-chip__line--v1 {
  top: 0;
  bottom: 0;
  left: 33%;
  width: 1px;
}

.emv-chip__line--v2 {
  top: 0;
  bottom: 0;
  right: 33%;
  width: 1px;
}

.emv-chip__core {
  position: absolute;
  top: 25%;
  bottom: 25%;
  left: 33%;
  right: 33%;
  border: 1px solid rgba(80, 50, 0, 0.45);
  border-radius: 2px;
}

.bank-card__nfc {
  font-size: 20px;
  color: rgba(255, 255, 255, 0.6);
}

/* Бренды платежных систем */
.brand-mir {
  font-family: system-ui, -apple-system, sans-serif;
  font-weight: 900;
  font-size: 18px;
  letter-spacing: 1px;
  color: #2bb673;
  text-shadow: 0 0 10px rgba(43, 182, 115, 0.4);
}

.brand-visa {
  font-family: system-ui, -apple-system, sans-serif;
  font-weight: 900;
  font-style: italic;
  font-size: 20px;
  letter-spacing: 1px;
  color: #ffffff;
  text-shadow: 0 0 10px rgba(255, 255, 255, 0.4);
}

.brand-mc {
  display: flex;
  align-items: center;
}

.brand-mc__circle {
  width: 22px;
  height: 22px;
  border-radius: 50%;
  opacity: 0.9;
}

.brand-mc__circle--red {
  background-color: #eb001b;
  margin-right: -10px;
}

.brand-mc__circle--orange {
  background-color: #f79e1b;
}

.brand-icon {
  font-size: 24px;
  color: rgba(255, 255, 255, 0.7);
}

/* Номер карты */
.bank-card__number {
  display: flex;
  align-items: center;
  gap: 12px;
  font-family: 'SF Mono', 'Roboto Mono', Menlo, monospace;
  font-size: 17px;
  letter-spacing: 2px;
  color: rgba(255, 255, 255, 0.95);
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.6);
}

.bank-card__number .dots {
  letter-spacing: 3px;
  font-size: 14px;
  opacity: 0.75;
}

.bank-card__number .digits {
  font-weight: 700;
  color: #fff;
}

/* Нижняя панель карты */
.bank-card__label {
  font-size: 8px;
  letter-spacing: 1px;
  opacity: 0.55;
  font-weight: 600;
}

.bank-card__value {
  font-family: 'SF Mono', 'Roboto Mono', Menlo, monospace;
  font-size: 13px;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.9);
}

/* Индикатор статуса */
.bank-card__status {
  padding: 4px 8px;
  background: rgba(0, 0, 0, 0.25);
  border-radius: 12px;
  border: 1px solid rgba(255, 255, 255, 0.08);
}

.status-dot {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background-color: #f87171;
  box-shadow: 0 0 6px #f87171;
}

.status-dot--active {
  background-color: #4ade80;
  box-shadow: 0 0 6px #4ade80;
}

.status-text {
  font-size: 11px;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.85);
}
</style>
